import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { Messages, AppRoutes } from 'config';
import { selectAuthUser } from 'store/slices/auth';
import { useUpdateMutation } from 'store/api/cart';
import { useMyCompanyInfoQuery } from 'store/api/company';
import { useAlert, isMobile, handleMutation } from 'hooks';
import { useCreateShippingMutation } from 'store/api/event';
import {
  formatPrice,
  calculateProductsPriceForApprovalDialog,
} from 'utils/helpers';
import {
  Drawer,
  Button,
  FormFooter,
  ButtonTypes,
  CommonStyles,
  ApprovalRequestDialog,
} from 'components';
import {
  useListQuery,
  useEditMutation,
  useCreateMutation,
  CustomerEditRequest,
  CustomerCreateRequest,
} from 'store/api/customers';

import * as Styles from './styles';
import { Package } from './package';
import { Products } from './products';
import { FormValues, ShipmentProps } from './types';
import { CustomerShipmentInfo } from './customer-shipment-info';
import {
  stepTitles,
  initialValues,
  mobileStepTitles,
  getValidationSchemas,
} from './config';

const { CheckOut } = AppRoutes.Products;

export const CheckoutShipment = ({
  event,
  opened,
  cartData,
  checkoutId,
  navigateUrl,
  setShipmentOpened,
  withMaxProductQuantity,
}: ShipmentProps) => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const { showSuccessAlert } = useAlert();
  const authUser = useAppSelector(selectAuthUser);
  const [currentStep, setCurrentStep] = useState(0);
  const [isInitialize, setInitialize] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);

  const { data: company } = useMyCompanyInfoQuery();
  const { data: customers } = useListQuery({ limit: 10000 });

  const [edit, editMutation] = useEditMutation();
  const [update, updateMutation] = useUpdateMutation();
  const [create, createdMutation] = useCreateMutation();
  const [createFreeShipping, createFreeShippingMutation] =
    useCreateShippingMutation();
  handleMutation(editMutation);
  handleMutation(updateMutation);
  handleMutation(createdMutation);
  handleMutation(createFreeShippingMutation);

  const isLastStep = currentStep === 2;
  const isFirstStep = currentStep === 0;
  const onClose = () => {
    setShipmentOpened(false);
    setInitialize(false);
  };

  const {
    isShowApprovalDialog,
    totalMinPriceProducts,
    totalCartPriceProducts,
  } = calculateProductsPriceForApprovalDialog({
    company,
    cartData,
    showApprovalDialog,
  });

  const saveCustomer = async (values: FormValues) => {
    const commonValues = {
      zip: values.zip,
      city: values.city,
      state: values.state,
      phone: values.phone,
      email: values.email,
      address: values.address,
    };

    if (values.isNewCustomer) {
      const payload = {
        ...commonValues,
        isFromShipping: true,
        employeeId: authUser?.id,
        lastName: values.lastName,
        firstName: values.firstName,
      } as CustomerCreateRequest;

      const { id } = await create(payload).unwrap();

      return id;
    }

    const existing = customers?.rows?.find(
      (customer) => customer.id === Number(values.recipientId)
    );

    if (existing?.person) {
      const payload = {
        ...existing,
        ...existing.person,
        ...commonValues,
        id: String(values.recipientId),
      } as CustomerEditRequest;

      await edit(payload).unwrap();
    }

    return Number(values.recipientId);
  };

  const shipProducts = async ({
    values,
    eventId,
    customerId,
  }: {
    eventId: number;
    values: FormValues;
    customerId: number;
  }) => {
    const tasks = values.products.map((item) =>
      update({
        ...item,
        id: eventId,
        isShipping: true,
        cartItemId: item.id,
        shipping: {
          customerId,
          price: +values.shippingPrice,
          isFreeShipping: values.isFreeShipping,
        },
      })
    );

    await Promise.all(tasks);
  };

  const availableProducts = useMemo(
    () => (withMaxProductQuantity ? cartData?.items : undefined),
    [cartData?.items, withMaxProductQuantity]
  );

  return (
    <Drawer opened={opened} topOffset="20%">
      <h2>Create shipment</h2>

      <Styles.StyledStepper
        active={currentStep}
        current={currentStep}
        onChange={setCurrentStep}
        steps={mobile ? mobileStepTitles : stepTitles}
      />

      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={getValidationSchemas(availableProducts)[currentStep]}
        onSubmit={async (formValues, { setTouched, setSubmitting }) => {
          if (!isLastStep) {
            setSubmitting(false);
            setCurrentStep((step) => step + 1);
            setTouched({
              zip: false,
              city: false,
              email: false,
              phone: false,
              state: false,
              address: false,
              lastName: false,
              firstName: false,
              recipientId: false,
              isNewCustomer: false,
            });
            return;
          }

          if (isShowApprovalDialog) {
            return setShowApprovalDialog(true);
          }

          try {
            const customerId = await saveCustomer(formValues);

            if (!navigateUrl) {
              await shipProducts({
                customerId,
                values: formValues,
                eventId: Number(event?.id),
              });
              console.log('[SHIPPING] create shipping', formValues);

              showSuccessAlert(`Shipping ${Messages.CREATED_SUCCESSFULLY}`);
              navigate(navigateUrl ?? CheckOut);
              onClose();
              return;
            }

            if (checkoutId && formValues.isFreeShipping) {
              const response = await createFreeShipping({
                ...formValues,
                customerId,
                id: checkoutId,
                paymentMethods: [],
                shippingPrice: +formValues.shippingPrice,
              }).unwrap();

              if (response.id) {
                onClose();
              }
              return;
            }

            navigate(navigateUrl, {
              state: {
                checkoutId,
                formValues: { ...formValues, recipientId: String(customerId) },
              },
            });
            onClose();
          } catch (error) {
            console.error(error);
          }
        }}
      >
        {({ dirty, values, handleSubmit, isSubmitting }) => (
          <CommonStyles.Form $hasMinHeight>
            <Styles.FormContainer>
              {isFirstStep && (
                <Products
                  opened={opened}
                  cartData={cartData}
                  isInitialize={isInitialize}
                  setInitialize={setInitialize}
                />
              )}
              {currentStep === 1 && (
                <CustomerShipmentInfo customers={customers?.rows} />
              )}
              {isLastStep && <Package />}
            </Styles.FormContainer>

            <FormFooter
              right={
                <Styles.ButtonWrapper>
                  <Button
                    fullWidth={mobile}
                    colorType={ButtonColors.LightBlue}
                    text={isFirstStep ? 'Cancel' : 'Back'}
                    onClick={
                      isFirstStep
                        ? onClose
                        : () => setCurrentStep((prevStep) => prevStep - 1)
                    }
                  />
                  <Button
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                    text={
                      isLastStep
                        ? `Checkout ${formatPrice(
                            values.products.reduce(
                              (acc, item) => acc + item.price * +item.quantity,
                              0
                            )
                          )}`
                        : 'Next'
                    }
                  />
                </Styles.ButtonWrapper>
              }
            />

            <ApprovalRequestDialog
              event={event}
              cart={cartData}
              open={showApprovalDialog}
              onSuccess={() => handleSubmit()}
              onClose={() => setShowApprovalDialog(false)}
              totalMinPriceProducts={totalMinPriceProducts}
              isFromShipping={!!navigateUrl && !!checkoutId}
              totalCartPriceProducts={totalCartPriceProducts}
            />
          </CommonStyles.Form>
        )}
      </Formik>
    </Drawer>
  );
};
